/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

/* eslint-env browser */

import isNil from 'lodash/isNil';

/**
 * Opravář [HTML video elementů](https://developer.mozilla.org/en-US/docs/Web/API/HTMLVideoElement).
 */
class HtmlVideoRepairer {
  /**
   * @param {string} blankSrc Zdroj videa reprezentující prázdný obsah.
   * Některé platformy mají problém s rušením `src`,
   * takže se místo toho nastavuje toto video.
   * @param {HtmlElementRepairer} elementRepairer
   * @param {HtmlMediaUtil} mediaUtil
   */
  constructor(blankSrc, elementRepairer, mediaUtil) {
    this.blankSrc = blankSrc;
    this.elementRepairer = elementRepairer;
    this.mediaUtil = mediaUtil;
  }

  // TODO Podporovat opravu source a track elementů.
  /**
   * Obnoví DOM element do výchozího stavu.
   * Za výchozí se považuje stav,
   * ve kterém je element po volání `document.createElement('video')`.
   *
   * Důležité je, aby bylo uvolněno maximální množství paměti.
   * Nastavují se pouze známé vlastnosti daného elementu,
   * ostatní jsou ignorovány.
   * Atributy elementu jsou odstraněny vždy.
   * Do obnovení není zahrnuta změna DOM struktury a propojení jejích uzlů,
   * kromě odebrání přímých potomků
   * [source elementů](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/source).
   *
   * Do obnovení nejsou zahrnuty vlastnosti pro callback funkce jako je
   * např. [onclick](https://developer.mozilla.org/en-US/docs/Web/API/GlobalEventHandlers/onclick).
   * Uživatel elementu by měl vždy využívat alternativní naslouchání pomocí
   * [EventTarget](https://developer.mozilla.org/en-US/docs/Web/API/EventTarget).
   * Uživatel je zároveň zodpovědný za to,
   * že všechny svoje naslouchání před opravou odebere.
   *
   * Neopravuje vlastnost [src](https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/src),
   * protože některé platformy (SSSP4) mají problémy s rušením `src` u video elementů.
   * Místo toho je `src` nastaveno na "blank" video tj. co nejmenší lokálně uložené video.
   * Ve výchozím stavu se používá černé MP4 video s vteřinovou dobou přehrávání.
   * Video bylo vytvořeno pomocí ffmpeg následujícím příkazem:
   * `ffmpeg -t 1 -f lavfi -i color=c=black:s=160x120 -c:v libx264 -tune stillimage -pix_fmt yuv420p output.mp4`
   *
   * Neumí opravit vlastnost
   * [currentSrc](https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/currentSrc).
   * Při prvním použití je `""`, po opravení bude nastaveno na "blank" video.
   * Nové Chrome (verze 72) opravu provedou správně,
   * ale starší (verze 31) bohužel ne.
   *
   * Neumí opravit vlastnost
   * [networkState](https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/networkState).
   * Při prvním použití je `NETWORK_EMPTY`,
   * po opravení je `NETWORK_NO_SOURCE`.
   *
   * Neumí opravit
   * [track elementy](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/track).
   * @param {HTMLMediaElement} el Element k opravení.
   * @returns {Promise.<undefined>} Vyřeší se, jakmile je element opraven.
   */
  repair(el) {
    // U atributů se spoléha na to, že jsou odstraněny v HtmlElementRepairer.
    // - autoplay, controls, loop, muted, preload
    return new Promise((resolve) =>
      resolve(
        this.repairSources(el)
          .then(() => this.repairSrc(el))
          .then(() => this.repairSrcObject(el))
          .then(() => {
            el.defaultPlaybackRate = 1;
            el.muted = false;
            el.playbackRate = 1;
            el.volume = 1;
          })
          .then(() =>
            this.elementRepairer.repair(el, { omitAttributes: ['src'] })
          )
      )
    );
  }

  /**
   * Zruší elementy <source>, pokud se v elementu el nachází
   * @param {HTMLMediaElement} el
   * @private
   * @returns {Promise.<undefined>}
   */
  repairSources(el) {
    return new Promise((resolve) =>
      resolve(
        Array.from(el.childNodes)
          .filter((node) => node.tagName === 'SOURCE')
          .forEach((item) => item.remove())
      )
    );
  }

  /**
   * Zruší vlastnost `src`, pokud ji má element nastavenou.
   * @param {HTMLMediaElement} el
   * @private
   * @returns {Promise.<undefined>}
   */
  repairSrc(el) {
    let onCanplay;
    let onError;
    return new Promise((resolve, reject) => {
      const { blankSrc } = this;
      if (!el.hasAttribute('src') || el.src === blankSrc) {
        return resolve();
      }
      onCanplay = () => resolve();
      onError = () => reject(this.mediaUtil.createError(el));
      el.addEventListener('canplay', onCanplay);
      el.addEventListener('error', onError);
      el.src = blankSrc;
      el.load();
    }).finally(() => {
      el.removeEventListener('canplay', onCanplay);
      el.removeEventListener('error', onError);
    });
  }

  /**
   * Zruší vlastnost `srcObject`, pokud ji má element nastavenou.
   * [Zdroj](https://developer.mozilla.org/en-US/docs/Web/API/MediaStreamTrack/stop#Stopping_a_video_stream).
   * @param {HTMLMediaElement} el
   * @private
   * @returns {Promise.<undefined>}
   */
  repairSrcObject(el) {
    return new Promise((resolve) => {
      const { srcObject } = el;
      if (isNil(srcObject)) {
        return resolve();
      }
      if (srcObject instanceof MediaStream) {
        srcObject.getTracks().forEach((track) => {
          track.stop();
        });
        el.srcObject = null;
        return resolve();
      }
      throw new Error(`Unknown srcObject "${srcObject}".`);
    });
  }
}

export default HtmlVideoRepairer;
